import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Navbar,
  Hero,
  Features,
  HowItWorks,
  Benefits,
  FAQ,
  FinalCTA,
  Footer,
} from "@/components/landing";

import type { SeedData, SectionKey } from "@/types/seed-data";
import seedDataJson from "../../../seed-data.json";

// Type the imported seed data
const seedData = seedDataJson as SeedData;

/**
 * Section Component Mapping
 *
 * Only the sections used in the current sectionOrder are registered here.
 * Dropped sections (stats, sponsors, blog, testimonials, pricing, appScreenshots,
 * featureSpotlight, appDemoVideo, appStoreDownload, integrationLogos) are not
 * referenced so their components remain unused by this page.
 *
 * The type is Partial<Record<SectionKey, ...>> because not all possible section
 * keys need a registered component — sectionOrder only lists the kept ones.
 */
const sectionComponents: Partial<
  Record<
    SectionKey,
    {
      Component: React.ComponentType<any>;
      getProps: (data: SeedData) => Record<string, unknown> | null;
      hasData: (data: SeedData) => boolean;
    }
  >
> = {
  features: {
    Component: Features,
    // Only pass structural data — ids and icons
    getProps: (data) => ({
      features: data.features,
    }),
    hasData: (data) => Boolean(data.features && data.features.length > 0),
  },
  howItWorks: {
    Component: HowItWorks,
    getProps: (data) => ({
      steps: data.howItWorks?.steps,
    }),
    hasData: (data) => Boolean(data.howItWorks?.steps && data.howItWorks.steps.length > 0),
  },
  benefits: {
    Component: Benefits,
    getProps: () => ({}),
    hasData: () => true,
  },
  faq: {
    Component: FAQ,
    getProps: () => ({}),
    hasData: () => true,
  },
  finalCTA: {
    Component: FinalCTA,
    getProps: () => ({}),
    hasData: () => true,
  },
};

export function LandingPage() {
  const { t } = useTranslation("landing");

  // Get section order from seed data, default to empty array if not defined
  const sectionOrder = seedData.sectionOrder ?? [];

  return (
    <div className="relative min-h-screen">
      {/* Core section: Navbar (always rendered) */}
      <Navbar companyName={t("common:org.name")} ctaText={t("hero.primaryCTA")} />

      <main>
        {/* Core section: Hero (always rendered) */}
        <Hero />

        {/**
         * Conditional Section Rendering
         *
         * Iterates over sectionOrder and renders each section if:
         * 1. The section key exists in the component mapping
         * 2. The section has data in seedData (checked via hasData)
         *
         * This allows the landing page to be fully data-driven:
         * - Remove a section: delete its key from sectionOrder
         * - Reorder sections: change the order in sectionOrder array
         * - Add a section: add its key and corresponding data
         */}
        {sectionOrder.map((sectionKey) => {
          const sectionConfig = sectionComponents[sectionKey];

          // Skip if section component doesn't exist or has no data
          if (!sectionConfig || !sectionConfig.hasData(seedData)) {
            return null;
          }

          const { Component, getProps } = sectionConfig;
          const props = getProps(seedData);

          // Skip if props couldn't be extracted
          if (!props) {
            return null;
          }

          return <Component key={sectionKey} {...props} />;
        })}
      </main>

      {/* Core section: Footer (always rendered) */}
      <Footer navigation={seedData.footer.navigation} social={seedData.footer.social} />
    </div>
  );
}

export default LandingPage;
